/**
 * Small building blocks shared by the Documents and AI pages (search with "/" shortcut, segmented
 * control, avatar, icon tile, file save). They sit on the UI foundation tokens — no page-local colours.
 */
import { useEffect, useLayoutEffect, useState } from 'react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';

/** Toolbar search; "/" focuses it from anywhere on the page — the shared one (components/ui/search-field). */
export { SearchField } from '@/components/ui/search-field';

/** One of N views. options: [{ key, label, count?, testId? }] */
export function Segmented({ options, value, onChange, ariaLabel, className }) {
  return (
    <div role="group" aria-label={ariaLabel} className={cn('inline-flex max-w-full shrink-0 gap-0.5 overflow-x-auto rounded-[11px] bg-[color:var(--status-gray-bg)] p-[3px]', className)}>
      {options.map((option) => {
        const active = option.key === value;
        return (
          <button
            key={option.key}
            type="button"
            aria-pressed={active}
            data-testid={option.testId}
            data-state={active ? 'on' : 'off'}
            onClick={() => onChange(option.key)}
            className={cn(
              'inline-flex h-[28px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 text-[12.5px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              active ? 'bg-surface text-ink shadow-card' : 'text-ink-2 hover:text-ink'
            )}
          >
            {option.label}
            {option.count !== undefined && option.count !== null ? (
              <span className="text-[11.5px] font-semibold tabular-nums text-ink-3">{option.count}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export { Avatar } from '@/components/ui/avatar-initials';

/** Tinted square with an icon (tone = foundation status tone). */
export function IconTile({ icon: Icon, tone = 'navy', size = 32, className, iconClassName, title }) {
  return (
    <span
      className={cn('ui-icon-tile', `tone-${tone}`, className)}
      style={{ width: size, height: size, borderRadius: size >= 32 ? 8 : 7 }}
      title={title}
    >
      {Icon ? <Icon className={cn(size >= 32 ? 'h-4 w-4' : 'h-[15px] w-[15px]', iconClassName)} aria-hidden="true" /> : null}
    </span>
  );
}

/** Saves a Blob as a file (the browser's download). */
export function saveBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export function saveText(text, fileName, type = 'text/csv;charset=utf-8') {
  // BOM so Excel opens UTF-8 CSV (Arabic names, "—") correctly.
  const body = type.startsWith('text/csv') ? `﻿${text}` : text;
  saveBlob(new Blob([body], { type }), fileName);
}

/** Downloads an authenticated file (`/api/...` url) under a readable name. */
export async function downloadAuthenticatedFile(fileUrl, fileName) {
  const apiPath = String(fileUrl || '').startsWith('/api/') ? String(fileUrl).slice(4) : String(fileUrl || '');
  const response = await api.get(apiPath, { responseType: 'blob' });
  saveBlob(response.data, fileName || apiPath.split('/').pop() || 'document');
}

/** Opens the OS file picker; resolves with the chosen File or null. Must run inside a user gesture. */
export function pickFile({ accept } = {}) {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    if (accept) input.accept = accept;
    input.style.position = 'fixed';
    input.style.left = '-9999px';
    let settled = false;
    const done = (file) => {
      if (settled) return;
      settled = true;
      input.remove();
      resolve(file || null);
    };
    input.addEventListener('change', () => done(input.files?.[0] || null));
    input.addEventListener('cancel', () => done(null));
    document.body.appendChild(input);
    input.click();
  });
}

/** Debounced copy of a value. */
export function useDebouncedValue(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/**
 * Which edges of a scroller hide content right now: { start, end } — left/right, or top/bottom with
 * `axis: 'y'`. Re-checked on scroll and when the scroller or its content changes size. `getNode` returns the
 * scroller (default: ref.current), so a scroller inside another component can be watched through a wrapper.
 * `watch` re-reads when the content changes (and picks up a scroller that mounted later).
 */
export function useScrollEdges(ref, { getNode, watch, axis = 'x' } = {}) {
  const [edges, setEdges] = useState({ start: false, end: false });
  useEffect(() => {
    const node = getNode ? getNode(ref.current) : ref.current;
    if (!node) {
      setEdges((current) => (current.start || current.end ? { start: false, end: false } : current));
      return undefined;
    }
    const vertical = axis === 'y';
    const update = () => {
      const room = vertical ? node.scrollHeight - node.clientHeight : node.scrollWidth - node.clientWidth;
      const position = vertical ? node.scrollTop : node.scrollLeft;
      const start = room > 1 && position > 1;
      const end = room > 1 && room - position > 1;
      setEdges((current) => (current.start === start && current.end === end ? current : { start, end }));
    };
    update();
    node.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(update) : null;
    if (observer) {
      observer.observe(node);
      if (node.firstElementChild) observer.observe(node.firstElementChild);
    }
    return () => {
      node.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      observer?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, watch, axis]);
  return edges;
}

/**
 * A soft fade on the edges that hide content — the hint that a row scrolls sideways (or, with axis 'y', that a
 * list goes on below / above).
 */
export function edgeFadeStyle({ start, end }, size = 28, axis = 'x') {
  if (!start && !end) return undefined;
  const direction = axis === 'y' ? 'to bottom' : 'to right';
  const mask = `linear-gradient(${direction}, ${start ? `transparent, black ${size}px` : 'black'}, ${end ? `black calc(100% - ${size}px), transparent` : 'black'})`;
  return { maskImage: mask, WebkitMaskImage: mask };
}

/**
 * [callback ref, rendered height in px] — 0 while nothing is attached or laid out (e.g. in tests). Read before the
 * first paint and again whenever the element is resized; follows the element when it mounts later.
 */
export function useElementHeight() {
  const [node, setNode] = useState(null);
  const [height, setHeight] = useState(0);
  useLayoutEffect(() => {
    if (!node) {
      setHeight(0);
      return undefined;
    }
    const update = () => {
      const next = Math.round(node.getBoundingClientRect().height);
      setHeight((current) => (current === next ? current : next));
    };
    update();
    if (typeof ResizeObserver !== 'function') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);
  return [setNode, height];
}

/** True while the media query matches. */
export function useMediaQuery(query) {
  const read = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? Boolean(window.matchMedia(query)?.matches)
    : false);
  const [matches, setMatches] = useState(read);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const list = window.matchMedia(query);
    if (!list) return undefined;
    const update = () => setMatches(Boolean(list.matches));
    update();
    if (list.addEventListener) list.addEventListener('change', update); else list.addListener?.(update);
    return () => {
      if (list.removeEventListener) list.removeEventListener('change', update); else list.removeListener?.(update);
    };
  }, [query]);
  return matches;
}

export const DOCUMENT_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.webp,.ttl';
